import React, { useState, useEffect } from "react";
import axios from "axios";

const base_url = process.env.NODE_ENV === "production" ? "https://hooks-28xd.onrender.com" : "http://localhost:4000";

const Kiosk = () => {
  const [todayWorkout, setTodayWorkout] = useState(null);
  const [displayedDate, setDisplayedDate] = useState("");
  const [actualToday, setActualToday] = useState("");
  const [currentTime, setCurrentTime] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    // Get current date in Mountain Time
    const getMountainTime = () => {
      const now = new Date();
      const mountainTime = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/Denver',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).format(now);
      
      // Convert MM/DD/YYYY to YYYY-MM-DD
      const [month, day, year] = mountainTime.split('/');
      return `${year}-${month}-${day}`;
    };

    // Fetch today's workout
    const fetchTodayWorkout = async () => {
      const today = getMountainTime();
      setDisplayedDate(today);
      setActualToday(today);
      setLoading(true);
      setError(null);
      
      try {
        const { data } = await axios.get(
          `${base_url}/api/workouts/daily/${today}`
        );
        
        if (data.success && data.workout) {
          setTodayWorkout(data.workout);
        } else {
          setTodayWorkout(null);
        }
        setLoading(false);
      } catch (error) {
        console.error("Error fetching today's workout:", error);
        setError("Failed to load today's workout");
        setLoading(false);
      }
    };

    // Calculate time until next 1 AM Mountain Time
    const getTimeUntilNext1AM = () => {
      const now = new Date();
      const mountainNow = new Date(now.toLocaleString("en-US", {timeZone: "America/Denver"}));
      
      // Create next 1 AM Mountain Time
      const next1AM = new Date(mountainNow);
      next1AM.setHours(1, 0, 0, 0);
      
      // If it's already past 1 AM today, set for tomorrow
      if (mountainNow.getHours() >= 1) {
        next1AM.setDate(next1AM.getDate() + 1);
      }
      
      // Convert back to local time for setTimeout
      const next1AMLocal = new Date(next1AM.toLocaleString("en-US", {timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone}));
      const timeUntil = next1AMLocal.getTime() - now.getTime();
      
      return Math.max(timeUntil, 0);
    };

    // Initial fetch
    fetchTodayWorkout();
    
    // Set up daily refresh at 1 AM Mountain Time
    const scheduleNext1AMRefresh = () => {
      const timeUntil1AM = getTimeUntilNext1AM();
      
      return setTimeout(() => {
        fetchTodayWorkout();
        
        // Schedule the next day's refresh (24 hours later)
        const dailyInterval = setInterval(fetchTodayWorkout, 24 * 60 * 60 * 1000);
        
        // Clean up function will handle clearing this interval
        return dailyInterval;
      }, timeUntil1AM);
    };

    const initialTimeout = scheduleNext1AMRefresh();
    
    return () => {
      clearTimeout(initialTimeout);
    };
  }, []);

  // Fetch workout for a specific date (moved outside useEffect)
  const fetchWorkoutForDate = async (date) => {
    setLoading(true);
    setError(null);
    
    try {
      const { data } = await axios.get(
        `${base_url}/api/workouts/daily/${date}`
      );
      
      if (data.success && data.workout) {
        setTodayWorkout(data.workout);
      } else {
        setTodayWorkout(null);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching workout:", error);
      setError("Failed to load workout");
      setLoading(false);
    }
  };

  // Navigation functions
  const goToPrevDay = () => {
    const prevDate = new Date(displayedDate + 'T00:00:00');
    prevDate.setDate(prevDate.getDate() - 1);
    const prevDateString = prevDate.toISOString().split('T')[0];
    setDisplayedDate(prevDateString);
    fetchWorkoutForDate(prevDateString);
  };

  const goToNextDay = () => {
    const nextDate = new Date(displayedDate + 'T00:00:00');
    nextDate.setDate(nextDate.getDate() + 1);
    const nextDateString = nextDate.toISOString().split('T')[0];
    setDisplayedDate(nextDateString);
    fetchWorkoutForDate(nextDateString);
  };

  const resetToToday = () => {
    setDisplayedDate(actualToday);
    fetchWorkoutForDate(actualToday);
  };

  const isShowingToday = displayedDate === actualToday;

  // Separate effect for updating current time display every second
  useEffect(() => {
    const updateCurrentTime = () => {
      const time = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/Denver',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      }).format(new Date());
      setCurrentTime(time);
    };

    // Update immediately
    updateCurrentTime();
    
    // Update every second
    const timeInterval = setInterval(updateCurrentTime, 1000);
    
    return () => clearInterval(timeInterval);
  }, []);
  const formatDate = (dateString) => {
    const options = { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      timeZone: 'America/Denver'
    };
    return new Date(dateString + 'T00:00:00').toLocaleDateString('en-US', options);
  };


  if (loading) {
    return (
      <div style={{
        maxWidth: '100%',
        margin: '0 auto',
        padding: '10px',
        fontFamily: '"JetBrains Mono", "Fira Code", "SF Mono", Consolas, monospace',
        backgroundColor: '#000000',
        color: '#c9d1d9',
        minHeight: '100vh'
      }}>
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <h1 style={{ fontSize: '1.5rem', margin: '0 0 15px 0', color: '#58a6ff' }}>$ loading workout.sh...</h1>
          <div style={{
            border: '2px solid #21262d',
            borderTop: '2px solid #58a6ff',
            borderRadius: '50%',
            width: '30px',
            height: '30px',
            animation: 'spin 1s linear infinite',
            margin: '0 auto'
          }}></div>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div style={{
        maxWidth: '100%',
        margin: '0 auto',
        padding: '10px',
        fontFamily: '"JetBrains Mono", "Fira Code", "SF Mono", Consolas, monospace',
        backgroundColor: '#000000',
        color: '#c9d1d9',
        minHeight: '100vh'
      }}>
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <h1 style={{ fontSize: '1.5rem', margin: '0 0 10px 0', color: '#f85149' }}>$ ERROR</h1>
          <p style={{ margin: 0, fontSize: '0.9rem', color: '#f85149' }}>{error}</p>
        </div>
      </div>
    );
  }
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: '#000000',
      color: '#c9d1d9',
      fontFamily: '"JetBrains Mono", "Fira Code", "SF Mono", Consolas, monospace',
      overflow: 'auto',
      padding: '10px'
    }}>
      <header style={{
        textAlign: 'left',
        marginBottom: '10px',
        padding: '8px 12px',
        backgroundColor: '#161b22',
        border: '1px solid #30363d',
        borderRadius: '6px',
        borderLeft: '3px solid #58a6ff'
      }}>
        <h1 style={{
          color: '#58a6ff',
          margin: '0 0 4px 0',
          fontSize: 'clamp(1.1rem, 3vw, 1.4rem)',
          fontWeight: '400'
        }}>$ ./today_workout.sh</h1>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <p style={{
              fontSize: 'clamp(0.75rem, 2vw, 0.85rem)',
              fontWeight: '400',
              margin: '0 0 2px 0',
              color: '#7d8590'
            }}># {formatDate(displayedDate)}</p>
            <p style={{
              fontSize: 'clamp(0.7rem, 1.8vw, 0.8rem)',
              color: '#7d8590',
              margin: 0
            }}># MT: {currentTime}</p>
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button
              onClick={goToPrevDay}
              style={{
                backgroundColor: '#21262d',
                border: '1px solid #30363d',
                color: '#c9d1d9',
                padding: '6px 12px',
                borderRadius: '4px',
                fontSize: 'clamp(0.7rem, 1.8vw, 0.8rem)',
                cursor: 'pointer',
                fontFamily: 'inherit'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#30363d'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#21262d'}
            >
              ← Prev Day
            </button>
            <button
              onClick={goToNextDay}
              style={{
                backgroundColor: '#21262d',
                border: '1px solid #30363d',
                color: '#c9d1d9',
                padding: '6px 12px',
                borderRadius: '4px',
                fontSize: 'clamp(0.7rem, 1.8vw, 0.8rem)',
                cursor: 'pointer',
                fontFamily: 'inherit'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#30363d'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#21262d'}
            >
              Next Day →
            </button>
            {!isShowingToday && (
              <button
                onClick={resetToToday}
                style={{
                  backgroundColor: '#1f2328',
                  border: '1px solid #58a6ff',
                  color: '#58a6ff',
                  padding: '6px 12px',
                  borderRadius: '4px',
                  fontSize: 'clamp(0.7rem, 1.8vw, 0.8rem)',
                  cursor: 'pointer',
                  fontFamily: 'inherit'
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = '#58a6ff';
                  e.target.style.color = '#000000';
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = '#1f2328';
                  e.target.style.color = '#58a6ff';
                }}
              >
                ← Today
              </button>
            )}
          </div>
        </div>
      </header>
      {todayWorkout ? (
        <div style={{
          backgroundColor: '#161b22',
          border: '1px solid #30363d',
          padding: '15px',
          borderRadius: '6px'
        }}>
          <div style={{
            textAlign: 'left',
            marginBottom: '15px',
            borderBottom: '1px solid #30363d',
            paddingBottom: '12px'
          }}>
            <h2 style={{
              color: '#f0f6fc',
              fontSize: 'clamp(1.2rem, 3vw, 1.5rem)',
              margin: '0 0 8px 0',
              fontWeight: '400'
            }}>## {todayWorkout.planName}</h2>
            {todayWorkout.description && (
              <p style={{
                color: '#7d8590',
                fontSize: 'clamp(0.85rem, 2vw, 0.95rem)',
                lineHeight: '1.4',
                margin: 0
              }}># {todayWorkout.description}</p>
            )}
          </div>
          {todayWorkout.activities && todayWorkout.activities.length > 0 ? (
            <div style={{ display: 'grid', gap: '12px', marginBottom: '15px' }}>
              {todayWorkout.activities.map((activity, index) => (
                <div key={index} style={{
                  backgroundColor: '#000000',
                  border: '1px solid #30363d',
                  padding: '12px',
                  borderRadius: '6px',
                  borderLeft: '3px solid #f79000'
                }}>
                  {activity.type && (
                    <h3 style={{
                      color: '#f79000',
                      fontSize: 'clamp(1rem, 2.5vw, 1.2rem)',
                      margin: '0 0 8px 0',
                      fontWeight: '400'
                    }}>### {activity.type}</h3>
                  )}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', fontSize: 'clamp(0.8rem, 2vw, 0.9rem)' }}>
                    {activity.duration && (
                      <span style={{ color: '#c9d1d9', backgroundColor: '#21262d', padding: '2px 8px', borderRadius: '3px', border: '1px solid #30363d' }}>
                        duration: {activity.duration}
                      </span>
                    )}
                    {activity.distance && (
                      <span style={{ color: '#c9d1d9', backgroundColor: '#21262d', padding: '2px 8px', borderRadius: '3px', border: '1px solid #30363d' }}>
                        distance: {activity.distance}
                      </span>
                    )}
                    {activity.pace && (
                      <span style={{ color: '#c9d1d9', backgroundColor: '#21262d', padding: '2px 8px', borderRadius: '3px', border: '1px solid #30363d' }}>
                        pace: {activity.pace}
                      </span>
                    )}
                  </div>
                  {activity.description && (
                    <p style={{ 
                      margin: '8px 0 0 0', 
                      color: '#7d8590', 
                      fontSize: 'clamp(0.8rem, 2vw, 0.9rem)',
                      lineHeight: '1.3'
                    }}>
                      # {activity.description}
                    </p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '20px', color: '#7d8590' }}>
              <h3 style={{ color: '#58a6ff', margin: '0 0 8px 0', fontSize: 'clamp(1rem, 2.5vw, 1.2rem)' }}>### Rest Day</h3>
              <p style={{ margin: 0, fontSize: 'clamp(0.8rem, 2vw, 0.9rem)' }}># No activities scheduled for today. Enjoy your rest!</p>
            </div>
          )}
          {todayWorkout.notes && (
            <div style={{
              backgroundColor: '#1f2328',
              border: '1px solid #d1242f',
              padding: '12px',
              borderRadius: '6px',
              borderLeft: '3px solid #d1242f'
            }}>
              <h3 style={{ 
                color: '#d1242f', 
                margin: '0 0 6px 0', 
                fontSize: 'clamp(0.9rem, 2.2vw, 1rem)',
                fontWeight: '400'
              }}>### NOTES</h3>
              <p style={{ 
                color: '#c9d1d9', 
                lineHeight: '1.4', 
                margin: 0,
                fontSize: 'clamp(0.8rem, 2vw, 0.9rem)'
              }}># {todayWorkout.notes}</p>
            </div>
          )}
        </div>
      ) : (
        <div style={{ 
          textAlign: 'center', 
          padding: '20px', 
          color: '#7d8590',
          backgroundColor: '#161b22',
          border: '1px solid #30363d',
          borderRadius: '6px'
        }}>
          <h2 style={{ 
            color: '#58a6ff', 
            margin: '0 0 8px 0', 
            fontSize: 'clamp(1.2rem, 3vw, 1.5rem)',
            fontWeight: '400'
          }}>## No Workout Scheduled</h2>
          <p style={{ margin: 0, fontSize: 'clamp(0.8rem, 2vw, 0.9rem)' }}># There is no workout assigned for today.</p>
        </div>
      )}    </div>
  );
};

export default Kiosk;